'use strict'

// Presentation when a page opens: show a banner or redirect.
// Showing or hiding a view belongs on that view. A stored hideView action is ignored.

const { matchesDisplayCondition } = require('./display-rules')

const LAYOUT_TYPES = new Set(['row', 'column'])
const PAGE_TITLE = /^[A-Za-z_][A-Za-z0-9_]*$/
const MAX_BANNERS = 20
const MAX_MESSAGE = 500

function pageOpenRuleList (page) {

  const stored = page && page.pageOpenRules

  if (Array.isArray(stored)) return stored
  if (stored && Array.isArray(stored.rules)) return stored.rules

  return []
}

function hasPageOpenRules (page) {

  return pageOpenRuleList(page).length > 0
}

function normalizeHost (value) {

  const raw = String(value || '').trim().toLowerCase()

  if (!raw || raw.includes('*') || /\s/.test(raw)) return ''

  const withScheme = raw.includes('://') ? raw : `https://${raw}`

  let parsed

  try {

    parsed = new URL(withScheme)
  } catch (err) {

    return ''
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return ''
  if (!parsed.hostname || parsed.username || parsed.password) return ''

  return parsed.hostname.toLowerCase()
}

function redirectHostAllowList (source) {

  const stored = source && source.redirectHostAllowList
  const raw = Array.isArray(stored) ? stored : String(stored || '').split(/[\s,]+/)
  const hosts = []
  const seen = new Set()

  for (const entry of raw) {

    const host = normalizeHost(entry)

    if (!host || seen.has(host)) continue

    seen.add(host)
    hosts.push(host)
  }

  return hosts
}

function redirectHostsFromPages (pages) {

  const hosts = []
  const seen = new Set()

  for (const page of pages || []) {

    for (const host of redirectHostAllowList(page)) {

      if (seen.has(host)) continue

      seen.add(host)
      hosts.push(host)
    }
  }

  return hosts
}

function hostsForRedirect (page, context) {

  if (context && Object.prototype.hasOwnProperty.call(context, 'redirectHosts')) {

    return redirectHostAllowList({ redirectHostAllowList: context.redirectHosts })
  }

  return redirectHostAllowList(page)
}

function externalUrlHost (url) {

  let parsed

  try {

    parsed = new URL(String(url || '').trim())
  } catch (err) {

    return ''
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return ''
  if (!parsed.hostname) return ''

  return parsed.hostname.toLowerCase()
}

function castValue (value, dataType) {

  if (dataType === 'number') {

    const num = Number(value)

    return Number.isNaN(num) ? value : num
  }

  if (dataType === 'checkbox') {

    if (value === 'true' || value === true) return true
    if (value === 'false' || value === false) return false

    return !!value
  }

  return value
}

function conditionMatches (condition, context) {

  if (!condition || !condition.field) return false
  if (condition.subject !== 'user' && condition.subject !== 'record') return false

  const row = condition.subject === 'user' ? context && context.user : context && context.record

  if (!row) return false

  const dataType = condition.dataType
  const value = dataType === 'number' || dataType === 'checkbox'
    ? castValue(row[condition.field], dataType)
    : row[condition.field]
  const expected = castValue(condition.value, dataType)

  return matchesDisplayCondition(value, condition.operator, expected, dataType)
}

function ruleMatches (rule, context) {

  if (!rule) return false

  const listed = Array.isArray(rule.conditions) ? rule.conditions : []
  const conditions = listed.filter((condition) => condition && (condition.subject === 'user' || condition.subject === 'record') && condition.field)

  if (!conditions.length) return listed.length === 0

  if ((rule.conditionMatchMode || 'all') === 'any') {

    return conditions.some((condition) => conditionMatches(condition, context))
  }

  return conditions.every((condition) => conditionMatches(condition, context))
}

function plainMessage (value) {

  return String(value == null ? '' : value).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, MAX_MESSAGE)
}

function ruleActions (rule) {

  if (Array.isArray(rule && rule.actions)) return rule.actions
  if (rule && rule.action && typeof rule.action === 'object') return [rule.action]

  return []
}

function pageViewChoices (page) {

  const views = []

  const walk = (nodes) => {

    for (const node of nodes || []) {

      if (!node || typeof node !== 'object') continue

      if (LAYOUT_TYPES.has(node.type)) {

        walk(node.components)
        walk(node.children)
        continue
      }

      if (node.uniqueTitle && node.type) {

        views.push({
          uniqueTitle: node.uniqueTitle,
          title: node.title || node.uniqueTitle,
          type: node.type
        })
      }
    }
  }

  walk(page && page.components)

  return views
}

function evaluatePageOpenRules (page, context) {

  const rules = pageOpenRuleList(page)
  const allowed = new Set(hostsForRedirect(page, context))
  const currentPage = page && page.uniqueTitle
  const banners = []
  const refusedRedirects = []
  let redirect = null

  for (const rule of rules) {

    if (!ruleMatches(rule, context || {})) continue

    ruleActions(rule).forEach((action, index) => {

      if (!action || !action.type || action.type === 'hideView') return

      if (action.type === 'banner') {

        const message = plainMessage(action.message)

        if (!message || banners.length >= MAX_BANNERS) return

        const id = action.id || `${rule.id || 'rule'}:${index}`

        banners.push({ id: String(id), message })
        return
      }

      if (action.type !== 'redirect') return

      const destination = action.destination
      const pageUniqueTitle = String(action.pageUniqueTitle || '').trim()
      const wantsPage = destination === 'page' || (destination !== 'external' && pageUniqueTitle && !action.url)

      if (wantsPage) {

        if (!PAGE_TITLE.test(pageUniqueTitle) || pageUniqueTitle === currentPage || redirect) return

        redirect = { pageUniqueTitle }
        return
      }

      const url = String(action.url || '').trim()
      const host = externalUrlHost(url)

      if (!host || !allowed.has(host)) {

        if (refusedRedirects.length < MAX_BANNERS) {

          refusedRedirects.push({
            url,
            host: host || '',
            reason: 'host_not_allowed'
          })
        }

        return
      }

      if (!redirect) redirect = { url }
    })
  }

  return { banners, redirect, refusedRedirects }
}

function presentLivePage (page, context) {

  if (!page || !hasPageOpenRules(page)) return page

  const outcome = evaluatePageOpenRules(page, context)
  const copy = JSON.parse(JSON.stringify(page))

  delete copy.pageOpenRules
  delete copy.redirectHostAllowList

  if (outcome.banners.length || outcome.redirect || outcome.refusedRedirects.length) {

    copy.pageOpen = {
      banners: outcome.banners,
      redirect: outcome.redirect,
      refusedRedirects: outcome.refusedRedirects
    }
  }

  return copy
}

function pageOpenRedirect (pageOpen, currentPageUniqueTitle) {

  const redirect = pageOpen && pageOpen.redirect

  if (!redirect) return null

  if (redirect.pageUniqueTitle) {

    if (redirect.pageUniqueTitle === currentPageUniqueTitle) return null
    if (!PAGE_TITLE.test(redirect.pageUniqueTitle)) return null

    return { pageUniqueTitle: redirect.pageUniqueTitle }
  }

  if (redirect.url && externalUrlHost(redirect.url)) return { url: redirect.url }

  return null
}

function pageRequestUrl (url, accessKey) {

  const key = Array.isArray(accessKey) ? accessKey[0] : accessKey

  if (!url || key == null || String(key) === '') return url

  const join = String(url).includes('?') ? '&' : '?'

  return `${url}${join}accessKey=${encodeURIComponent(String(key))}`
}

module.exports = {
  pageOpenRuleList,
  hasPageOpenRules,
  normalizeHost,
  redirectHostAllowList,
  redirectHostsFromPages,
  externalUrlHost,
  conditionMatches,
  ruleMatches,
  pageViewChoices,
  evaluatePageOpenRules,
  presentLivePage,
  pageOpenRedirect,
  pageRequestUrl
}
